import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Calendar, MapPin, Users, Clock, ArrowRight, Sparkles, Mic, Zap, Lock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TrackCard({ dd, index, onApply }) {
  const isFull = dd.max_attendees && dd.attendee_count >= dd.max_attendees;
  return (
    <div className="group relative dd-reveal" style={{ animationDelay: `${Math.min(index * 110, 600)}ms` }} data-testid={`public-track-${dd.id}`}>
      <div className="absolute -inset-px pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.25) 0%, transparent 40%, transparent 60%, rgba(0,229,255,0.15) 100%)" }} />
      <div className="relative bg-gradient-to-br from-white/[0.02] to-transparent border border-white/[0.07] h-full flex flex-col" style={{ borderLeft: "3px solid #a855f7" }}>
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-[#a855f7]/50 text-[#a855f7] bg-[#a855f7]/10">
              <Zap size={10} /> Deep Dive
            </span>
            <span className="flex items-center gap-1 text-[12px] text-white/50"><Clock size={11} />{dd.time} Uhr</span>
            {dd.max_attendees && (
              <span className="flex items-center gap-1 text-[12px] text-white/45">
                <Users size={11} />{dd.attendee_count}/{dd.max_attendees} Plätze
              </span>
            )}
          </div>
          <h3 className="font-outfit font-bold text-xl text-white tracking-tight leading-snug mb-3">{dd.title}</h3>
          <p className="text-[14px] text-white/60 leading-relaxed flex-1 whitespace-pre-wrap line-clamp-6">{dd.description}</p>
          {dd.speakers?.length > 0 && (
            <div className="mt-4 flex items-start gap-2">
              <Mic size={13} className="text-[#a855f7]/80 mt-0.5 shrink-0" />
              <div className="text-[12px] text-white/70">
                {dd.speakers.map((s, i) => (
                  <span key={`${s.name}-${i}`}>
                    <span className="text-white font-outfit font-semibold">{s.name}</span>
                    {s.company && <span className="text-white/40"> · {s.company}</span>}
                    {i < dd.speakers.length - 1 && <span className="text-white/30">, </span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5 pt-5 border-t border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
            {isFull ? (
              <span className="text-[12px] text-amber-400/80 font-outfit font-semibold uppercase tracking-wider">Ausgebucht · Bewerbung möglich</span>
            ) : (
              <span className="text-[12px] text-white/40 font-outfit uppercase tracking-wider">Mit Bewerbung · Plätze begrenzt</span>
            )}
            <Button onClick={() => onApply(dd)} data-testid={`public-apply-${dd.id}`}
              className="bg-[#a855f7] text-white hover:bg-[#a855f7] hover:shadow-[0_0_32px_-4px_rgba(168,85,247,0.7)] hover:-translate-y-0.5 rounded-none h-11 px-5 font-outfit font-semibold text-[13px] tracking-wide transition-all">
              Jetzt bewerben <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginGateModal({ open, onClose, festivalId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="public-login-gate">
      <div className="relative max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="absolute -inset-px pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.4) 0%, transparent 50%, rgba(168,85,247,0.2) 100%)" }} />
        <div className="relative bg-[#0a0a0a] border border-white/10 p-7 sm:p-9">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white" data-testid="public-gate-close"><X size={18} /></button>
          <div className="flex items-center gap-2 mb-3">
            <Lock size={13} className="text-[#00e5ff]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/90 font-outfit font-semibold">Nur für Community-Mitglieder</span>
          </div>
          <h3 className="font-outfit font-bold text-2xl text-white tracking-tight mb-3">Bewerbung nur für registrierte Unternehmer</h3>
          <p className="text-[14px] text-white/65 leading-relaxed mb-6">
            Die Deep-Dive-Tracks sind Teil der geschlossenen <b className="text-white">[KI]NPUT Community</b> im Illertal.
            Registriere dich kostenlos — ein Admin prüft dein Profil und gibt dich frei. Anschließend kannst du dich direkt für deinen Track bewerben.
          </p>
          <div className="space-y-2">
            <Link to={`/?redirect=${encodeURIComponent("/events/festival/" + festivalId)}`}
              data-testid="public-gate-register"
              className="block w-full text-center bg-[#00e5ff] text-black hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none h-12 font-outfit font-semibold text-[14px] tracking-wide leading-[48px] transition-all hover:-translate-y-0.5">
              Kostenlos registrieren →
            </Link>
            <Link to={`/?redirect=${encodeURIComponent("/events/festival/" + festivalId)}&auth=signin`}
              data-testid="public-gate-login"
              className="block w-full text-center bg-transparent text-white border border-white/15 hover:border-[#00e5ff]/60 hover:text-[#00e5ff] rounded-none h-11 font-outfit font-semibold text-[13px] tracking-wide leading-[44px] transition-colors">
              Schon Mitglied? Anmelden
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FestivalPublicPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [gateOpen, setGateOpen] = useState(false);

  // If already logged in, bounce to the authenticated festival page
  useEffect(() => {
    if (user && id) navigate(`/events/festival/${id}`, { replace: true });
  }, [user, id, navigate]);

  useEffect(() => {
    if (user) return; // redirecting
    axios.get(`${API}/public/festival/${id}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Festival nicht gefunden"));
  }, [id, user]);

  const dayStr = useMemo(() => data?.date ? new Date(data.date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : "", [data]);

  // Build OG image + canonical URL for SEO
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonicalSlug = data?.slug || id;
  const canonical = origin ? `${origin}/festival/${canonicalSlug}` : "";
  const ogImage = data?.flyer_url
    ? `${process.env.REACT_APP_BACKEND_URL}${data.flyer_url}`
    : `${origin}/og-default.png`;
  const ogTitle = data?.title ? `${data.title} · [KI]NPUT` : "[KI]NPUT Festival";
  const ogDescription = data?.description
    ? (data.description.slice(0, 180) + (data.description.length > 180 ? "…" : ""))
    : "Das KI-Festival für den Mittelstand im Illertal.";

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <X size={32} className="text-red-400/70 mx-auto mb-3" />
          <h1 className="font-outfit font-bold text-2xl mb-2">Festival nicht gefunden</h1>
          <p className="text-white/50 mb-6">{err}</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 h-11 border border-[#00e5ff]/40 text-[#00e5ff] font-outfit font-semibold text-[13px] uppercase tracking-wider hover:bg-[#00e5ff]/10" data-testid="public-back-home">
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00e5ff]/60 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const onApply = (dd) => setGateOpen(true);  // eslint-disable-line no-unused-vars

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        {canonical && <link rel="canonical" href={canonical} />}
        <meta property="og:type" content="event" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        {canonical && <meta property="og:url" content={canonical} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="[KI]NPUT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[55%] h-[60%] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-[55%] h-[60%] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10) 0%, transparent 70%)" }} />
      </div>

      {/* Minimal top bar (no authenticated navbar) */}
      <header className="relative z-10 border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white font-outfit font-black tracking-tight text-lg" data-testid="public-brand">
            <span className="text-[#00e5ff]">[KI]</span>NPUT
          </Link>
          <div className="flex items-center gap-2">
            <Link to={`/?redirect=${encodeURIComponent("/events/festival/" + id)}&auth=signin`}
              className="px-3 sm:px-4 h-9 text-[12px] uppercase tracking-wider text-white/60 hover:text-[#00e5ff] font-outfit font-semibold leading-[36px] transition-colors" data-testid="public-top-login">
              Anmelden
            </Link>
            <Link to={`/?redirect=${encodeURIComponent("/events/festival/" + id)}`}
              className="px-3 sm:px-4 h-9 text-[12px] uppercase tracking-wider bg-[#00e5ff] text-black font-outfit font-semibold leading-[36px] hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.6)] transition-all" data-testid="public-top-register">
              Registrieren
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

          {/* Hero */}
          <div className="relative border border-white/[0.08] overflow-hidden mb-10" data-testid="public-festival-hero">
            {/* 4K-Hintergrundvideo (HD/Mobile split), Fallback auf Flyer */}
            <div className="absolute inset-0">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_poster.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
              <video
                autoPlay muted loop playsInline preload="metadata"
                poster={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_poster.jpg`}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="public-festival-hero-video"
              >
                <source src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_mobile.mp4`} type="video/mp4" media="(max-width: 768px)" />
                <source src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg.mp4`} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/45" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
            <div className="relative p-7 sm:p-10 lg:p-12 min-h-[420px] flex flex-col justify-end">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#a855f7]" />
                <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#a855f7] font-semibold">Festival · Öffentliche Ankündigung</span>
                {data.deep_dives?.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-outfit font-bold border border-[#00e5ff]/50 text-[#00e5ff] bg-[#00e5ff]/10">
                    <Sparkles size={10} /> {data.deep_dives.length} Tracks
                  </span>
                )}
              </div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-white mb-5 max-w-3xl" data-testid="public-festival-title">
                {data.title}
              </h1>
              <p className="text-[16px] sm:text-lg text-white/65 max-w-2xl leading-relaxed mb-6 whitespace-pre-wrap">{data.description}</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/55 font-outfit">
                {dayStr && <span className="inline-flex items-center gap-2"><Calendar size={13} className="text-[#00e5ff]" />{dayStr}</span>}
                {data.time && <span className="inline-flex items-center gap-2"><Clock size={13} className="text-[#00e5ff]" />ab {data.time} Uhr</span>}
                {data.location && <span className="inline-flex items-center gap-2"><MapPin size={13} className="text-[#00e5ff]" />{data.location}</span>}
              </div>
            </div>
          </div>

          {/* Member-only note */}
          <div className="mb-8 border-l-2 border-[#00e5ff]/70 bg-[#00e5ff]/[0.04] p-4 flex items-start gap-3" data-testid="public-community-note">
            <Lock size={16} className="text-[#00e5ff] shrink-0 mt-0.5" />
            <p className="text-[13px] text-white/75 leading-relaxed">
              Die Deep-Dive-Tracks sind Teil der geschlossenen <b className="text-white">[KI]NPUT Community</b>.
              Bewerbungen nehmen wir nur von registrierten Mitgliedern an — die Registrierung ist kostenlos und binnen 24 h freigeschaltet.
            </p>
          </div>

          {/* Tracks */}
          <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-px bg-[#a855f7]/60" />
                <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#a855f7]/90 font-semibold">Das Programm</span>
              </div>
              <h2 className="font-outfit font-black text-2xl sm:text-3xl tracking-tight text-white">Deep-Dive Tracks</h2>
            </div>
          </div>

          {data.deep_dives?.length === 0 ? (
            <div className="text-center py-20 border border-white/[0.06] bg-white/[0.01]" data-testid="public-no-tracks">
              <Mic size={28} className="text-[#a855f7]/50 mx-auto mb-3" strokeWidth={1.3} />
              <p className="text-white/75 text-[15px] font-outfit font-semibold">Das Programm wird aktuell kuratiert</p>
              <p className="text-white/40 text-[13px] mt-1">Schau bald wieder vorbei — die Tracks werden in Kürze veröffentlicht.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="public-tracks-grid">
              {data.deep_dives.map((dd, i) => (
                <TrackCard key={dd.id} dd={dd} index={i} onApply={onApply} />
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-14 border border-white/[0.08] bg-gradient-to-br from-[#00e5ff]/[0.04] via-white/[0.015] to-[#a855f7]/[0.04] p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" data-testid="public-bottom-cta">
            <div>
              <h3 className="font-outfit font-bold text-2xl text-white tracking-tight mb-2">Bereit für deinen Track?</h3>
              <p className="text-[14px] text-white/60 max-w-lg">Werde Community-Mitglied, vervollständige dein Profil und bewirb dich in wenigen Minuten für einen der Deep Dives.</p>
            </div>
            <Link to={`/?redirect=${encodeURIComponent("/events/festival/" + id)}`}
              className="inline-flex items-center gap-2 px-6 h-12 bg-[#00e5ff] text-black font-outfit font-semibold text-[14px] tracking-wide hover:shadow-[0_0_40px_-4px_rgba(0,229,255,0.7)] hover:-translate-y-0.5 transition-all" data-testid="public-bottom-register">
              Jetzt registrieren <ArrowRight size={15} />
            </Link>
          </div>

          <footer className="mt-10 pt-6 border-t border-white/[0.06] text-[11px] text-white/35 font-outfit flex flex-wrap items-center justify-between gap-2">
            <span>Powered by <b className="text-white/60">[KI]NPUT</b> · teamWERK Erolzheim</span>
            <Link to="/legal" className="hover:text-[#00e5ff]">Impressum & Datenschutz</Link>
          </footer>
        </div>
      </main>

      <LoginGateModal open={gateOpen} onClose={() => setGateOpen(false)} festivalId={id} />

      <style>{`
        .dd-reveal { opacity: 0; transform: translateY(18px); animation: dd-rise 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes dd-rise { to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .dd-reveal { animation: none; opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
